import {Definition, SimpleDefinition} from './definition';
import {ActorType} from './enumerations/actor-type';
import {DeleteResourcesTargetType} from './enumerations/delete-resources-target-type';
import {PowerShellInputType} from './enumerations/power-shell-input-type';
import {PowerShellReturnType} from './enumerations/power-shell-return-type';
import {PowerShellScriptLocation} from './enumerations/power-shell-script-location';
import {LogOnType} from './enumerations/log-on-type';
import {MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {ActivityDialog} from './activities/dialogs/activity-dialog';
import {from as observableFrom, Observable} from 'rxjs';
import {stripXMLPrefix} from './ctrl-processdesigner.component';
import {ActivityPropertyDataType} from './enumerations/activity-property-data-type';
import {IDGeneratorType} from './enumerations/id-generator-type';
import {LazyDialogService} from './activities/lazy-dialog.service';
import {mergeMap} from 'rxjs/operators';

export abstract class Activity {
  public ActivityDisplayName: string;
  public Name: string;
  public readonly classType: ActivityType;

  protected constructor(
    private lazyDialogService: LazyDialogService,
    className: ActivityType,
    activityNode?: any,
  ) {
    this.classType = className;
    if (this.classType !== ActivityType.IDGenerator) {
      this._properties['ActivityDisplayName'] = ActivityPropertyDataType.STRING;
    }
    this._properties['Name'] = ActivityPropertyDataType.STRING;

    this.dialogConfig = new MatDialogConfig();
    this.dialogConfig.disableClose = true;

    if (activityNode != null) {
      this.parse(activityNode);
    }
  }

  protected _properties: any = Object.create(null);

  get properties(): any {
    return this._properties;
  }

  protected _advancedProperties: string[];

  get className(): string {
    return ActivityType[this.classType];
  }

  get isUnknown(): boolean {
    return false;
  }

  get hasAdvanced(): boolean {
    return Object.keys(this._properties).indexOf('Advanced') !== -1;
  }

  protected dialogConfig: MatDialogConfig;

  abstract openDialog(): Observable<any>;

  beforeSave(): void {
    if (this['Advanced'] !== true && this._advancedProperties) {
      this._advancedProperties.forEach((prop: string) => {
        this[prop] = null;
      });
    }

    // remove empty definitions/array elements
    for (let prop in this._properties) {
      if (
        this._properties[prop] === ActivityPropertyDataType.DEFINITION_TABLE ||
        this._properties[prop] === ActivityPropertyDataType.STRING_ARRAY
      ) {
        if (this[prop] == null || this[prop].length === 0) continue;
        for (let i = 0; i < this[prop].length; i++) {
          if (this[prop][i].isEmpty()) {
            this[prop].splice(i, 1);
            i--;
          }
        }
      }
    }
  }

  protected beforeEdit(): void {
    for (let prop in this._properties) {
      if (this._properties[prop] === ActivityPropertyDataType.DEFINITION_TABLE) {
        if (this[prop] == null) {
          this[prop] = [new Definition()];
        } else if (this[prop].length === 0) {
          this[prop].push(new Definition());
        }
      } else if (this._properties[prop] === ActivityPropertyDataType.STRING_ARRAY) {
        if (this[prop] == null) {
          this[prop] = [new SimpleDefinition()];
        } else if (this[prop].length === 0) {
          this[prop].push(new SimpleDefinition());
        }
      }
    }
  }

  protected openDialogLazy(component, config: MatDialogConfig): Observable<any> {
    if (component == null || this.lazyDialogService == null) return null;
    return observableFrom(this.lazyDialogService.openDialog(component, this.dialogConfig)).pipe(
      mergeMap(ref => this.openDialogRef(ref)),
    );
  }

  protected openDialogRef(dialogRef: MatDialogRef<ActivityDialog>): Observable<any> {
    // call with this.dialog.open(ActivityDialogType)

    this.beforeEdit();

    dialogRef.componentInstance.activity = this;
    return dialogRef.afterClosed();
  }

  protected parse(activityNode: any) {
    if (activityNode == null) {
      return;
    }

    // process attributes
    for (let xmlKey in activityNode.$) {
      let key = stripXMLPrefix(xmlKey);
      if (!(key in this._properties)) {
        console.warn(
          'Invalid workflow XOML, property ' +
            key +
            ' not known in activity ' +
            this.ActivityDisplayName,
        );
        continue;
      }

      try {
        const value = activityNode.$[xmlKey];
        switch (this._properties[key]) {
          case ActivityPropertyDataType.STRING:
            this[key] = value;
            break;
          case ActivityPropertyDataType.HTMLSTRING:
            this[key] = value.replace('&#xA;', '\r\n');
            break;
          case ActivityPropertyDataType.BOOLEAN:
            this[key] = value.toLowerCase() === 'true';
            break;
          case ActivityPropertyDataType.INTEGER:
            this[key] = parseInt(value);
            break;
          case ActivityPropertyDataType.ACTOR_TYPE:
            this[key] = ActorType[value];
            break;
          case ActivityPropertyDataType.DELETE_RESOURCES_TARGET_TYPE:
            this[key] = DeleteResourcesTargetType[value];
            break;
          case ActivityPropertyDataType.IDGENERATOR_TYPE:
            this[key] = IDGeneratorType[value];
            break;
          case ActivityPropertyDataType.LOG_ON_TYPE:
            this[key] = LogOnType[value];
            break;
          case ActivityPropertyDataType.POWER_SHELL_INPUT_TYPE:
            this[key] = PowerShellInputType[value];
            break;
          case ActivityPropertyDataType.POWER_SHELL_RETURN_TYPE:
            this[key] = PowerShellReturnType[value];
            break;
          case ActivityPropertyDataType.POWER_SHELL_SCRIPT_LOCATION:
            this[key] = PowerShellScriptLocation[value];
            break;
          case ActivityPropertyDataType.STRING_ARRAY:
            if (value === '{x:Null}') {
              this[key] = null;
            } else {
              throw (
                'Invalid XOML format, can not parse ' +
                this.className +
                '.' +
                key +
                ' as ' +
                ActivityPropertyDataType[this._properties[key]]
              );
            }
            break;
          default:
            console.warn(
              'Parsing ' +
                this.className +
                '.' +
                key +
                ' as ' +
                ActivityPropertyDataType[this._properties[key]] +
                ' not supported yet.',
            );
            break;
        }
      } catch (e) {
        console.error('Failed parsing workflow xml attribute ' + key, activityNode.$[xmlKey]);
      }
    }

    // process elements
    if (activityNode.elements) {
      for (let elem of activityNode.elements) {
        let name = stripXMLPrefix(elem.name).split('.').pop();

        if (!(name in this._properties)) {
          console.warn(
            'Invalid workflow XOML, property ' +
              name +
              ' not known in activity ' +
              this.ActivityDisplayName,
          );
          continue;
        }

        switch (this._properties[name]) {
          case ActivityPropertyDataType.DEFINITION_TABLE:
            if (
              elem.elements == null ||
              elem.elements.length !== 1 ||
              stripXMLPrefix(elem.elements[0].name) !== 'Hashtable' ||
              elem.elements[0].elements.length === 0
            ) {
              console.warn(
                'Hashtable in XOML for property ' +
                  name +
                  ' not found in activity ' +
                  this.ActivityDisplayName,
              );
              continue;
            }

            this[name] = [];

            const table = elem.elements[0].elements;
            let count = null;

            for (let entry of table) {
              let key, value;

              for (let valEntry of entry.elements) {
                if (valEntry.type === 'text') {
                  value = valEntry.text;
                } else if (valEntry.type === 'element' && stripXMLPrefix(valEntry.name) === 'Key') {
                  key = valEntry.elements[0].elements[0].text;
                }
              }

              if (key === undefined && value === undefined) {
                continue;
              }

              if (key === 'Count') {
                count = value;
                continue;
              }

              const keyParts = key.split(':');
              let row: number = parseInt(keyParts[0]);
              let col: string = keyParts[1];

              if (this[name][row] == null) {
                this[name][row] = new Definition();
              }

              if (col === '0') {
                this[name][row].left = value;
              } else if (col === '1') {
                this[name][row].right = value;
              } else if (col === '2') {
                this[name][row].check = value.toLowerCase() === 'true';
              }
            }

            if (parseInt(count) != this[name].length) {
              console.error(
                'Parsing Workflow: Count != Definition Table Length',
                count,
                this[name].length,
              );
            }

            break;
          case ActivityPropertyDataType.STRING_ARRAY:
            if (
              elem.elements == null ||
              elem.elements.length !== 1 ||
              stripXMLPrefix(elem.elements[0].name) !== 'ArrayList'
            ) {
              console.warn(
                'ArrayList in XOML for property ' +
                  name +
                  ' not found in activity ' +
                  this.ActivityDisplayName,
              );
              continue;
            }

            this[name] = [];
            const list = elem.elements[0].elements;

            for (let entry of list) {
              if (entry.type !== 'element') {
                console.warn(
                  'Cannot process ArrayList Entry of ' + this.ActivityDisplayName + '.' + name,
                  entry,
                );
                continue;
              }

              if (stripXMLPrefix(entry.name) !== 'String') {
                console.warn(
                  'Cannot process non-String ArrayList Entry of ' +
                    this.ActivityDisplayName +
                    '.' +
                    name,
                  entry,
                );
                continue;
              }

              if (entry.elements == null || entry.elements.length !== 1) {
                console.warn(
                  'Cannot process ArrayList Entry of ' + this.ActivityDisplayName + '.' + name,
                  entry,
                );
              }

              const value = entry.elements[0].text;
              this[name].push(new SimpleDefinition(value));
            }

            break;
          default:
            console.warn(
              'Parsing ' + name + ' as ' + this._properties[name] + ' not supported yet.',
            );
            break;
        }
      }
    }
  }

  public objForXML(): any {
    let obj = {
      name: 'ns1ID:' + this.className,
      type: 'element',
      $: {
        'x:Name': this.Name == null ? '' : this.Name,
      },
      elements: [],
    };
    if (this.classType === ActivityType.IDGenerator) {
      obj = {
        name: 'ns3ID:' + this.className,
        type: 'element',
        $: {
          'x:Name': this.Name == null ? '' : this.Name,
        },
        elements: [],
      };
    }

    for (let prop in this._properties) {
      if (prop === 'Name') continue;

      const value = this[prop];
      switch (this._properties[prop]) {
        case ActivityPropertyDataType.STRING:
          obj.$[prop] = value == null ? '' : value;
          break;
        case ActivityPropertyDataType.HTMLSTRING:
          obj.$[prop] = value == null ? '' : value;
          break;
        case ActivityPropertyDataType.BOOLEAN:
          obj.$[prop] = value === true ? 'True' : 'False';
          break;
        case ActivityPropertyDataType.INTEGER:
          obj.$[prop] = value == null ? '' : '' + value;
          break;
        case ActivityPropertyDataType.ACTOR_TYPE:
          obj.$[prop] = value == null ? ActorType[ActorType.Service] : ActorType[value];
          break;
        case ActivityPropertyDataType.DELETE_RESOURCES_TARGET_TYPE:
          obj.$[prop] = value == null ? '' : DeleteResourcesTargetType[value];
          break;
        case ActivityPropertyDataType.IDGENERATOR_TYPE:
          obj.$[prop] = value == null ? '' : IDGeneratorType[value];
          break;
        case ActivityPropertyDataType.LOG_ON_TYPE:
          obj.$[prop] = value == null ? '' : LogOnType[value];
          break;
        case ActivityPropertyDataType.POWER_SHELL_INPUT_TYPE:
          obj.$[prop] = value == null ? '' : PowerShellInputType[value];
          break;
        case ActivityPropertyDataType.POWER_SHELL_RETURN_TYPE:
          obj.$[prop] = value == null ? '' : PowerShellReturnType[value];
          break;
        case ActivityPropertyDataType.POWER_SHELL_SCRIPT_LOCATION:
          obj.$[prop] = value == null ? '' : PowerShellScriptLocation[value];
          break;
        case ActivityPropertyDataType.STRING_ARRAY:
          if (value == null) {
            obj.$[prop] = '{x:Null}';
          } else if (Array.isArray(value)) {
            const arrObj = {
              name: 'ns1ID:' + this.className + '.' + prop,
              type: 'element',
              elements: [
                {
                  name: 'ns5ID:ArrayList',
                  type: 'element',
                  $: {
                    'xmlns:ns5ID':
                      'clr-namespace:System.Collections;Assembly=mscorlib, Version=4.0.0.0, Culture=neutral, PublicKeyToken=b77a5c561934e089',
                  },
                  elements: [],
                },
              ],
            };
            obj.elements.push(arrObj);

            value.forEach((val: SimpleDefinition) => {
              const arrEntry = {
                name: 'ns4ID:String',
                type: 'element',
                elements: [{type: 'text', text: val.left}],
              };
              arrObj.elements[0].elements.push(arrEntry);
            });
          }
          break;
        case ActivityPropertyDataType.DEFINITION_TABLE:
          const tableObj = {
            name: 'ns1ID:' + this.className + '.' + prop,
            type: 'element',
            elements: [
              {
                name: 'ns5ID:Hashtable',
                type: 'element',
                $: {
                  'xmlns:ns5ID':
                    'clr-namespace:System.Collections;Assembly=mscorlib, Version=4.0.0.0, Culture=neutral, PublicKeyToken=b77a5c561934e089',
                },
                elements: [],
              },
            ],
          };
          obj.elements.push(tableObj);

          const countObj = {
            name: 'ns4ID:Int32',
            type: 'element',
            elements: [
              {type: 'text', text: value == null ? '0' : '' + value.length},
              {
                type: 'element',
                name: 'x:Key',
                elements: [
                  {
                    type: 'element',
                    name: 'ns4ID:String',
                    elements: [
                      {
                        type: 'text',
                        text: 'Count',
                      },
                    ],
                  },
                ],
              },
            ],
          };

          tableObj.elements[0].elements.push(countObj);

          if (value != null && Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
              const def: Definition = value[i];
              const leftObj = {
                name: 'ns4ID:String',
                type: 'element',
                elements: [
                  {type: 'text', text: def.left == null ? '' : def.left},
                  {
                    type: 'element',
                    name: 'x:Key',
                    elements: [
                      {
                        type: 'element',
                        name: 'ns4ID:String',
                        elements: [
                          {
                            type: 'text',
                            text: '' + i + ':0',
                          },
                        ],
                      },
                    ],
                  },
                ],
              };
              const rightObj = {
                name: 'ns4ID:String',
                type: 'element',
                elements: [
                  {type: 'text', text: def.right == null ? '' : def.right},
                  {
                    type: 'element',
                    name: 'x:Key',
                    elements: [
                      {
                        type: 'element',
                        name: 'ns4ID:String',
                        elements: [
                          {
                            type: 'text',
                            text: '' + i + ':1',
                          },
                        ],
                      },
                    ],
                  },
                ],
              };
              const checkObj = {
                name: 'ns4ID:Boolean',
                type: 'element',
                elements: [
                  {type: 'text', text: def.check === true ? 'true' : 'false'},
                  {
                    type: 'element',
                    name: 'x:Key',
                    elements: [
                      {
                        type: 'element',
                        name: 'ns4ID:String',
                        elements: [
                          {
                            type: 'text',
                            text: '' + i + ':2',
                          },
                        ],
                      },
                    ],
                  },
                ],
              };

              tableObj.elements[0].elements.push(leftObj);
              tableObj.elements[0].elements.push(rightObj);
              tableObj.elements[0].elements.push(checkObj);
            }
          }

          break;
        default:
          console.warn(
            'Exporting objForXML for ' +
              this.className +
              '.' +
              prop +
              ' as ' +
              ActivityPropertyDataType[this._properties[prop]] +
              ' not supported yet.',
          );
          break;
      }
    }

    return obj;
  }
}

export enum ActivityType {
  AddDelay,
  CreateResource,
  DeleteResources,
  GenerateUniqueValue,
  RequestApproval,
  RunPowerShellScript,
  SendEmailNotification,
  UpdateResources,
  VerifyRequest,
  IDGenerator,
  Custom,
  UNKNOWN = -1,
}
